import { Controller } from "stimulus";
import { openDisclosure, toggleDisclosure, closeOverlay, isOpen } from "../../training_grooming_calendar/overlay";
import { resetDetail, activeCount, resettable } from "../../training_grooming_calendar/url_state";
import { COLLAPSED, toggleSection, sectionsAfterChange, claimReopen, menuMaxHeight, scrollToShow, serviceSummary, statusSummary, categorySummary, checkedAfterReset, facilitySelection, showAllFocusKey } from "../../training_grooming_calendar/filter_sections";

let pending = null;
let sections = COLLAPSED;

function selectedFacility(menu) {
  const option = menu.querySelector('[data-name="facility"][aria-selected="true"]');
  return option ? option.dataset.value : null;
}

function selectFacility(root, value) {
  const options = Array.from(root.querySelectorAll('[data-name="facility"]'));
  const name = (option) => option.querySelector(".tg-facility-option-name");
  const view = facilitySelection(options.map((option) => ({ value: option.dataset.value, label: name(option) ? name(option).textContent : "" })), value);
  if (!view) return;
  options.forEach((option, index) => option.setAttribute("aria-selected", String(view.selected[index])));
  const label = root.querySelector(".tg-facility-value");
  if (label) label.textContent = view.label;
}

export default class extends Controller {
  static targets = ["button", "menu"];

  connect() {
    const loaded = this.element.closest("[data-tg-load-reason]");
    const claim = claimReopen(pending, loaded ? loaded.dataset.tgLoadReason : null);
    pending = null;
    sections = claim.sections;
    this.applySections();
    if (!claim.open) return;
    this.open();
    const focus = claim.focusKey && this.menuTarget.querySelector(`[data-focus-key="${claim.focusKey}"]`);
    if (focus) focus.focus({ preventScroll: true });
    const list = this.categoryList();
    if (list) list.scrollTop = claim.listScroll;
  }

  refreshCounts(state) {
    const count = activeCount(state, this.hasCustomOrder());
    const badge = this.element.querySelector(".tg-filters-count");
    if (badge) {
      badge.textContent = String(count);
      badge.dataset.count = String(count);
      badge.setAttribute("aria-label", `${count} active`);
      badge.hidden = count === 0;
    }
    this.buttonTarget.classList.toggle("tg-filters-active", count > 0);
    this.element.querySelectorAll(".tg-filters-reset-all").forEach((button) => {
      button.hidden = !resettable(state);
    });
    const showAll = this.element.querySelector(".tg-filters-show-all");
    if (showAll) showAll.hidden = (state.hiddenStatuses || []).length === 0;
    const clear = this.element.querySelector(".tg-filters-clear");
    if (clear) {
      const checked = (state.categories || []).length;
      clear.textContent = `Clear (${checked})`;
      clear.hidden = checked === 0;
    }
  }

  hasCustomOrder() {
    const reset = this.element.querySelector(".tg-filters-reset");
    return !!reset && !reset.hidden;
  }

  categoryList() {
    return this.menuTarget.querySelector(".tg-filters-categories");
  }

  listScroll() {
    const list = this.categoryList();
    return list ? list.scrollTop : 0;
  }

  disconnect() {
    if (isOpen(this.menuTarget)) closeOverlay({ restoreFocus: false });
  }

  toggle() {
    if (toggleDisclosure(this.disclosure())) this.fitMenu();
  }

  open() {
    openDisclosure(this.disclosure());
    this.fitMenu();
  }

  scrolled() {
    this.menuTarget.classList.toggle("tg-filters-scrolled", this.menuTarget.scrollTop > 0);
  }

  fitMenu() {
    if (this.element.classList.contains("tg-filters-sheet")) return;
    const top = this.menuTarget.getBoundingClientRect().top;
    this.menuTarget.style.setProperty("--tg-filters-max", `${menuMaxHeight({ top, viewport: window.innerHeight })}px`);
  }

  disclosure() {
    return {
      element: this.menuTarget,
      trigger: this.buttonTarget,
      root: this.element.closest(".tg"),
      sheet: this.element.classList.contains("tg-filters-sheet"),
      triggerClass: "tg-filters-open",
    };
  }

  close() {
    closeOverlay();
  }

  pick(event) {
    const { name, value } = event.currentTarget.dataset;
    this.menuTarget.querySelectorAll(`[data-name="${name}"]`).forEach((option) => option.setAttribute("aria-checked", option.dataset.value === value ? "true" : "false"));
    sections = sectionsAfterChange(sections, name);
    if (name === "service") this.showService(event.currentTarget);
    this.change(event.currentTarget, name === "service" ? "services-summary" : undefined);
  }

  showService(option) {
    this.applySections();
    this.refreshServiceSummary(option);
    const summary = this.menuTarget.querySelector('.tg-filters-summary[data-section-toggle="services"]');
    if (summary) summary.focus({ preventScroll: true });
  }

  refreshServiceSummary(option) {
    const summary = this.menuTarget.querySelector('.tg-filters-summary[data-section-toggle="services"]');
    if (!summary || !option) return;
    const label = option.querySelector(".tg-filters-option-label");
    const view = serviceSummary(option.dataset.value, label ? label.textContent : "");
    const dot = summary.querySelector(".tg-filters-dot");
    if (dot) dot.className = view.dotClass;
    const text = summary.querySelector(".tg-filters-summary-text");
    if (text) text.textContent = view.label;
  }

  refreshSummaries() {
    this.refreshServiceSummary(this.menuTarget.querySelector('[data-name="service"][aria-checked="true"]'));
    this.refreshSummary("status", "statuses", statusSummary);
    this.refreshSummary("category", "categories", categorySummary);
  }

  toggleSection(event) {
    const section = event.currentTarget.dataset.sectionToggle;
    sections = toggleSection(sections, section);
    this.applySections();
    const toggle = this.menuTarget.querySelector(`.tg-filters-change[data-section-toggle="${section}"]`);
    if (toggle) toggle.focus({ preventScroll: true });
    if (!sections[section]) return;
    const element = this.menuTarget.querySelector(`[data-filters-section="${section}"]`);
    if (element) this.menuTarget.scrollTop += scrollToShow(element.getBoundingClientRect(), this.menuTarget.getBoundingClientRect());
  }

  applySections() {
    this.element.querySelectorAll("[data-filters-section]").forEach((element) => {
      const open = !!sections[element.dataset.filtersSection];
      element.classList.toggle("tg-section-open", open);
      element.querySelectorAll("[data-section-toggle]").forEach((toggle) => toggle.setAttribute("aria-expanded", open ? "true" : "false"));
    });
  }

  flip(event) {
    const toggle = event.currentTarget;
    toggle.setAttribute("aria-checked", toggle.getAttribute("aria-checked") === "true" ? "false" : "true");
    this.change(toggle);
  }

  pickFacility(event) {
    selectFacility(this.menuTarget, event.currentTarget.dataset.value);
    this.change(event.currentTarget, "facility");
  }

  showAllStatuses(event) {
    const rows = this.menuTarget.querySelectorAll('[data-name="status"]');
    rows.forEach((option) => option.setAttribute("aria-checked", "true"));
    this.change(event.currentTarget, showAllFocusKey(!!sections.statuses, rows[0] && rows[0].dataset.focusKey));
  }

  refreshSummary(name, section, summarize) {
    const rows = Array.from(this.menuTarget.querySelectorAll(`[data-name="${name}"]`));
    const text = this.menuTarget.querySelector(`.tg-filters-summary[data-section-toggle="${section}"] .tg-filters-summary-text`);
    if (rows.length === 0 || !text) return;
    const label = (row) => row.querySelector(".tg-filters-option-label");
    const view = summarize(rows.map((row) => ({ label: label(row) ? label(row).textContent : "", checked: row.getAttribute("aria-checked") === "true" })));
    text.textContent = view.label;
    text.classList.toggle("tg-filters-summary-active", view.active);
    if (view.shown) this.showStatusIcons(text, view.shown.map((index) => rows[index]));
  }

  showStatusIcons(text, rows) {
    const generic = text.parentElement.querySelector(".tg-filters-summary-icon");
    if (generic) generic.hidden = rows.length > 0;
    if (rows.length === 0) return;
    text.textContent = "";
    rows.forEach((row, index) => {
      if (index > 0) text.append(", ");
      const item = document.createElement("span");
      item.className = "tg-filters-summary-status";
      const icon = row.querySelector(".tg-status");
      if (icon) item.append(icon.cloneNode(true));
      const label = row.querySelector(".tg-filters-option-label");
      item.append(label ? label.textContent.trim() : "");
      text.append(item);
    });
  }

  clearCategories(event) {
    this.menuTarget.querySelectorAll('[data-name="category"]').forEach((option) => option.setAttribute("aria-checked", "false"));
    this.change(event.currentTarget);
  }

  resetAll() {
    const detail = resetDetail();
    this.menuTarget.querySelectorAll("[data-name]").forEach((row) => {
      const checked = checkedAfterReset(detail, row.dataset.name, row.dataset.value);
      if (checked !== null) row.setAttribute("aria-checked", String(checked));
    });
    selectFacility(this.menuTarget, detail.facility);
    sections = COLLAPSED;
    this.applySections();
    this.refreshCounts(detail);
    this.refreshSummaries();
    pending = { focusKey: "lanes-all", sections, listScroll: 0 };
    this.element.dispatchEvent(new CustomEvent("training-grooming-filters:change", { bubbles: true, detail }));
  }

  resetOrder(event) {
    pending = { focusKey: "lanes-all", sections, listScroll: this.listScroll() };
    event.currentTarget.dispatchEvent(new CustomEvent("training-grooming-filters:reset-order", { bubbles: true }));
  }

  change(source, focusKey = source.dataset.focusKey) {
    const picked = (name) => this.menuTarget.querySelector(`[data-name="${name}"][aria-checked="true"]`);
    const on = (name) => picked(name) !== null;
    const detail = {
      lanes: picked("lanes").dataset.value,
      service: picked("service").dataset.value,
      blocks: on("blocks") ? "show" : "hide",
      schedules: on("schedules") ? "show" : "hide",
      scheduled: on("scheduled") ? "only" : "all",
      facility: selectedFacility(this.menuTarget),
      categories: Array.from(this.menuTarget.querySelectorAll('[data-name="category"][aria-checked="true"]')).map((option) => option.dataset.value),
      hiddenStatuses: Array.from(this.menuTarget.querySelectorAll('[data-name="status"][aria-checked="false"]')).map((option) => option.dataset.value),
    };
    pending = { focusKey, sections, listScroll: this.listScroll() };
    this.refreshCounts(detail);
    this.refreshSummaries();
    this.element.dispatchEvent(new CustomEvent("training-grooming-filters:change", { bubbles: true, detail }));
  }
}
